import { useAuth0 } from '@auth0/auth0-vue';
import { setDatadogRumGlobalContextProperty, setDatadogRumUser } from '@marketspark/ui';
import { jwtDecode } from 'jwt-decode';
import { defineStore } from 'pinia';
// TODO:
// Figure out facade implementation
// for org switching
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import { MARKETSPARK, type AuthErrorKey } from '@/constants';
import { ROUTE_NAMES } from '@/router/routes.constants';
import {
  RoleName,
  type Organization,
  type User,
  type Location,
  type LocationAggregatedView,
} from '@/types';
import { api } from '@/utils/api';

export const currentUserStore = defineStore('currentUser', () => {
  const { getAccessTokenSilently } = useAuth0();
  const loggingIn = ref(false);

  const user = ref<User>({} as User);

  const organization = ref<Organization>(user.value?.organization || ({} as Organization));

  const permissions = ref<string[]>([]);

  const setPermissions = (perms: string[]) => {
    permissions.value = perms;
  };

  function setDetails(details: User) {
    user.value = details;
    organization.value = details.organization || ({} as Organization);
    setDatadogRumUser({ id: details.id, name: details.name, email: details.username });
    setDatadogRumGlobalContextProperty('organizationId', details.organizationId);
  }

  function setOrg(org: Organization) {
    user.value.organization = org;
    user.value.organizationId = org.id;
    organization.value = org;
    setDatadogRumGlobalContextProperty('organizationId', org.id);
  }

  async function loginUser() {
    // Test if the api is live
    let error: AuthErrorKey | null = 'NO_API';

    try {
      const { data } = await api.get('heartbeat');

      if (!data || data?.statusCode !== 200) {
        error = 'NO_API';
        throw new Error();
      }

      loggingIn.value = true;
      const token = await getAccessTokenSilently();

      if (!token) {
        error = 'TOKEN_ERROR';
        throw new Error();
      }

      const { permissions } = jwtDecode<{ permissions: string[] }>(token);

      setPermissions(permissions);

      const { data: dbUser } = await api.patch('/users/login', { isPendingConfirm: true });

      if (!dbUser) {
        error = 'NO_USER_DT';
        throw new Error();
      }

      setDetails(dbUser);
      return {
        status: 'success',
      };
    } catch {
      return {
        status: 'error',
        message: error,
      };
    } finally {
      loggingIn.value = false;
    }
  }

  const isMsOrg = computed(() => user?.value?.username.includes('@marketspark.com'));

  const isCurrentMsOrg = computed(() => user?.value?.organization?.name === MARKETSPARK);

  const isSuperAdmin = computed(() =>
    user?.value?.authRoles?.some(role => role.name === RoleName.SuperAdmin),
  );

  const isOrgAdmin = computed(() =>
    user?.value?.authRoles?.some(role => role.name === RoleName.OrgAdmin),
  );

  const isLocationAdmin = computed(() =>
    user.value.authRoles.some(role => role.name === RoleName.LocationAdmin),
  );

  const isUser = computed(() => user?.value?.authRoles?.some(role => role.name === RoleName.User));

  const isOrgAdminOrUser = computed(() => isOrgAdmin.value || isUser.value);

  const isSuperOrOrgAdmin = computed(() => isSuperAdmin.value || isOrgAdmin.value);

  const userOrgId = computed(() => user.value.organizationId);

  function isOrgView(location: Location | LocationAggregatedView) {
    return location.organizationId === organization.value.id;
  }

  return {
    loginUser,
    loggingIn,
    permissions,
    user,
    userOrgId,
    organization,
    setPermissions,
    setDetails,
    setOrg,
    isMsOrg,
    isCurrentMsOrg,
    isSuperAdmin,
    isOrgAdmin,
    isLocationAdmin,
    isUser,
    isOrgAdminOrUser,
    isSuperOrOrgAdmin,
    isOrgView,
  };
});

export const authenticateUserGuard = async () => {
  const { loginUser } = currentUserStore();
  const router = useRouter();
  const loginResult = await loginUser();
  if (loginResult.status === 'error') {
    router.push({
      name: ROUTE_NAMES.ERROR_PAGE,
      query: {
        msg: loginResult.message,
      },
    });
  }
};
